import React from 'react';
import {
  Radio as MuiRadio,
  RadioGroup,
  RadioProps as MuiRadioProps,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { StyledRadio, StyledFormControl } from './Radio.style';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioProps extends Omit<MuiRadioProps, 'onChange'> {
  /**
   * The label for the radio
   */
  label?: string;
  /**
   * Array of radio options for RadioGroup
   */
  options?: RadioOption[];
  /**
   * The current value
   */
  value?: string;
  /**
   * Callback fired when a radio is selected
   */
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the radio is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field has an error
   */
  error?: boolean;
  /**
   * Helper text to display below the radio group
   */
  helperText?: string;
  /**
   * The size of the radio
   */
  size?: 'small' | 'medium';
  /**
   * The color of the radio
   */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Whether the radio group is required
   */
  required?: boolean;
  /**
   * Position of the label relative to the radio
   */
  labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
  /**
   * The layout direction of the radio group
   */
  row?: boolean;
  /**
   * Label for the radio group
   */
  groupLabel?: string;
}

export const Radio: React.FC<RadioProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  color = 'primary',
  required = false,
  labelPlacement = 'end',
  row = false,
  groupLabel,
  ...props
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value, event);
    }
  };

  const radioElement = (
    <StyledRadio
      value={value}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      color={color}
      required={required}
      {...props}
    />
  );

  // Single radio usage
  if (label && !options) {
    return (
      <FormControlLabel
        control={radioElement}
        label={label}
        labelPlacement={labelPlacement}
        disabled={disabled}
        required={required}
      />
    );
  }

  // Radio group usage
  if (options) {
    return (
      <StyledFormControl error={error} disabled={disabled} required={required}>
        {groupLabel && (
          <FormLabel component="legend">
            {groupLabel}
          </FormLabel>
        )}
        
        <RadioGroup
          value={value}
          onChange={handleChange}
          row={row}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <StyledRadio
                  size={size}
                  color={color}
                  disabled={option.disabled || disabled}
                />
              }
              label={option.label}
              labelPlacement={labelPlacement}
              disabled={option.disabled || disabled}
            />
          ))}
        </RadioGroup>
        
        {helperText && (
          <FormHelperText>
            {helperText}
          </FormHelperText>
        )}
      </StyledFormControl>
    );
  }

  return radioElement;
}; 